/* NYIGEN | Header hover navigation v23
   Initial hover needs 220ms; switching between open menus is immediate.
   Native desktop Ignite hover is blocked; CSS, mobile and markup are unchanged. */
(() => {
  'use strict';
  const ROOT = '.header__inline-menu';
  const ITEM = `${ROOT} header-menu`;
  const PANEL = ':scope > .header__submenu, :scope > .mega-menu__content, :scope > .mega-menu__list-wrapper';
  const desktop = matchMedia('(min-width: 1400px) and (hover: hover) and (pointer: fine)');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const EXIT_DURATION = 240;
  const OUTSIDE_DELAY = 35;
  const OPEN_INTENT_DELAY = 220; // First opening: short deliberate hover.
  const data = new WeakMap();
  let current = null;
  const all = () => [...document.querySelectorAll(ITEM)];
  const details = m => m?.mainDetailsToggle || m?.querySelector(':scope > details');
  const summary = m => m?.summary || details(m)?.querySelector(':scope > summary');
  const panel = m => details(m)?.querySelector(PANEL);
  const state = m => {
    if (!data.has(m)) data.set(m, { leave: 0, finish: 0, intent: 0 });
    return data.get(m);
  };
  const clear = (m, field) => {
    const s = state(m);
    if (s[field]) clearTimeout(s[field]);
    s[field] = 0;
  };
  const cancel = m => { clear(m, 'leave'); clear(m, 'finish'); };
  const cancelIntent = m => clear(m, 'intent');
  const cancelAllIntents = () => all().forEach(cancelIntent);
  const inside = (r, x, y, extra = 0) => !!r && x >= r.left - extra && x <= r.right + extra && y >= r.top - extra && y <= r.bottom + extra;
  // Treat the ENTIRE strip between the logo and header icons as one
  // continuous navigation hover target. Gaps between sibling <li> elements
  // are deliberately included, even when hit-testing returns the header.
  function inNavCorridor(x, y) {
    const nav = current?.closest(ROOT);
    if (!nav) return false;
    const header = nav.closest('.header');
    if (!header) return false;
    const h = header.getBoundingClientRect();
    const brand = header.querySelector('.header__heading, .header__heading-link');
    const actions = header.querySelector('.header__icons');
    const brandBox = brand?.getBoundingClientRect();
    const actionBox = actions?.getBoundingClientRect();
    if (brandBox && inside(brandBox, x, y)) return false;
    if (actionBox && inside(actionBox, x, y)) return false;
    const navBox = nav.getBoundingClientRect();
    const left = brandBox ? brandBox.right + 2 : navBox.left;
    const right = actionBox ? actionBox.left - 2 : navBox.right;
    const pBox = panel(current)?.getBoundingClientRect();
    const bridgeBottom = pBox ? Math.max(h.bottom + 48, pBox.top + 16) : h.bottom + 48;
    return x >= left && x <= right && y >= h.top && y <= bridgeBottom;
  }
  function inCurrentPanel(x, y, target) {
    if (!current) return false;
    const p = panel(current);
    return !!p && (p.contains(target) || inside(p.getBoundingClientRect(), x, y, 3));
  }
  function finish(m) {
    cancel(m);
    cancelIntent(m);
    const d = details(m);
    if (d) d.open = false;
    summary(m)?.setAttribute('aria-expanded', 'false');
    m.classList.remove('open', 'nyigen-menu-closing', 'nyigen-menu-switching');
    if (current === m) current = null;
  }
  function close(m = current, instant = false) {
    if (!m || !details(m)?.open) return;
    if (!instant && m === current && mouseWithinSafeRegion()) {
      clear(m, 'leave');
      return;
    }
    clear(m, 'leave');
    if (instant || motion.matches) return finish(m);
    if (m.classList.contains('nyigen-menu-closing')) return;
    m.classList.add('nyigen-menu-closing');
    clear(m, 'finish');
    state(m).finish = setTimeout(() => {
      if (m === current && mouseWithinSafeRegion()) {
        m.classList.remove('nyigen-menu-closing');
        clear(m, 'finish');
        return;
      }
      finish(m);
    }, EXIT_DURATION);
  }
  function open(m) {
    if (!desktop.matches || !details(m)) return;
    cancelAllIntents();
    const switching = !!current && current !== m && details(current)?.open;
    if (current === m && details(m)?.open) {
      if (m.classList.contains('nyigen-menu-closing')) return;
      cancel(m);
      m.classList.remove('nyigen-menu-closing');
      return;
    }
    for (const other of all()) if (other !== m && details(other)?.open) finish(other);
    cancel(m);
    m.classList.remove('nyigen-menu-closing');
    if (switching) m.classList.add('nyigen-menu-switching');
    else m.classList.remove('nyigen-menu-switching');
    details(m).open = true;
    summary(m)?.setAttribute('aria-expanded', 'true');
    current = m;
  }
  // Only the first opening waits for a deliberate hover.
  // Handoff between open dropdowns is immediate.
  function requestHoverOpen(m) {
    if (!desktop.matches) return;
    cancelAllIntents();
    if (current === m && details(m)?.open) {
      open(m);
      return;
    }
    // Once a dropdown is open, switch instantly without a second hover timer.
    if (current && details(current)?.open) {
      open(m);
      return;
    }
    state(m).intent = setTimeout(() => {
      state(m).intent = 0;
      if (!desktop.matches || !pointerKnown) return;
      const s = summary(m);
      const hit = document.elementFromPoint(pointerX, pointerY);
      if (s && hit && (s === hit || s.contains(hit))) open(m);
    }, OPEN_INTENT_DELAY);
  }
  function scheduleOutside() {
    if (!current) return;
    const m = current;
    if (state(m).leave || m.classList.contains('nyigen-menu-closing')) return;
    state(m).leave = setTimeout(() => {
      state(m).leave = 0;
      if (current === m && !mouseWithinSafeRegion()) close(m);
    }, OUTSIDE_DELAY);
  }
  let pointerX = -1, pointerY = -1, pointerKnown = false;
  function updatePointer(e) {
    if (!Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) return;
    pointerX = e.clientX;
    pointerY = e.clientY;
    pointerKnown = true;
  }
  function mouseWithinSafeRegion() {
    return pointerKnown && (inNavCorridor(pointerX, pointerY) ||
      inCurrentPanel(pointerX, pointerY, document.elementFromPoint(pointerX, pointerY)));
  }
  function checkDeparture(e) {
    if (!desktop.matches || !current) return;
    if (e?.pointerType && e.pointerType !== 'mouse') return;
    if (e) updatePointer(e);
    if (mouseWithinSafeRegion()) clear(current, 'leave');
    else scheduleOutside();
  }
  function bind(m) {
    if (m.dataset.nyigenPremiumV23) return;
    const d = details(m), s = summary(m);
    if (!d || !s) return;
    m.dataset.nyigenPremiumV23 = '1';
    clearTimeout(m.hoverIntentTimer);
    clearTimeout(m.hoverCloseTimer);
    for (const [node, event, prop] of [
      [m, 'mouseenter', 'mouseEnterHandler'],
      [m, 'mouseleave', 'mouseLeaveHandler'],
      [s, 'mouseleave', 'mouseLeaveSummaryHandler'],
      [s, 'mouseover', 'mouseOverSummaryHandler']
    ]) if (node && typeof m[prop] === 'function') node.removeEventListener(event, m[prop]);
    m.classList.remove('open');
    m.style.removeProperty('--link-end');
    m.style.removeProperty('--header-zindex');
    m.setHoverBridge = () => {};
    s.addEventListener('pointerenter', e => {
      if (desktop.matches && e.pointerType === 'mouse') {
        updatePointer(e);
        requestHoverOpen(m);
      }
    });
    // NEW: Cancel a pending open the moment the pointer leaves the summary.
    s.addEventListener('pointerleave', e => {
      if (e.pointerType === 'mouse') cancelIntent(m);
    });
    s.addEventListener('click', e => {
      if (!desktop.matches) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cancelAllIntents();
      if (current === m && d.open && !m.classList.contains('nyigen-menu-closing')) close(m);
      else open(m);
    }, true);
    const p = panel(m);
    p?.addEventListener('pointerleave', checkDeparture);
  }
  // Ignite can register its own hover callbacks. Prevent ONLY native mouse
  // hover events from reaching desktop dropdown triggers; our pointerenter
  // listener still receives real pointer events. Panel links are unaffected.
  // This also covers a different Ignite version whose handler properties
  // aren't exposed on the header-menu instance.
  function blockNativeTriggerHover(e) {
    if (!desktop.matches || !(e.target instanceof Element)) return;
    const trigger = e.target.closest(`${ITEM} > details > summary`);
    const host = e.target.closest(ITEM);
    if (trigger || (host && !panel(host)?.contains(e.target))) {
      e.stopPropagation();
    }
  }
  document.addEventListener('mouseover', blockNativeTriggerHover, true);
  document.addEventListener('mouseenter', blockNativeTriggerHover, true);
  function init() { if (desktop.matches) all().forEach(bind); }
  document.addEventListener('pointermove', e => {
    if (!desktop.matches || e.pointerType !== 'mouse') return;
    updatePointer(e);
    // Fallback for switching: some browser/layout transitions do not deliver
    // a fresh pointerenter when crossing from one open dropdown to another.
    // A different summary switches instantly when a panel is open.
    const hoveredSummary = e.target instanceof Element
      ? e.target.closest(`${ITEM} > details > summary`) : null;
    const hoveredMenu = hoveredSummary?.closest('header-menu');
    if (hoveredMenu && hoveredMenu !== current && !state(hoveredMenu).intent) {
      requestHoverOpen(hoveredMenu);
    }
    if (current) checkDeparture(e);
  }, { passive: true });
  document.addEventListener('mouseleave', () => {
    pointerKnown = false;
    cancelAllIntents();
    if (current) scheduleOutside();
  });
  window.addEventListener('blur', () => {
    cancelAllIntents();
    if (current) close(current, true);
  });
  document.addEventListener('pointerdown', e => {
    if (!desktop.matches) return;
    cancelAllIntents();
    if (!current) return;
    const row = current.closest(ROOT);
    if (!row?.contains(e.target) && !panel(current)?.contains(e.target)) close(current, true);
  }, true);
  document.addEventListener('keydown', e => {
    if (!desktop.matches || e.key !== 'Escape') return;
    cancelAllIntents();
    if (!current) return;
    const s = summary(current);
    close(current, true);
    s?.focus();
  });
  document.addEventListener('focusin', e => {
    if (!desktop.matches) return;
    cancelAllIntents();
    if (current && !current.closest(ROOT)?.contains(e.target)) close(current, true);
  });
  desktop.addEventListener('change', () => {
    cancelAllIntents();
    if (!desktop.matches) { for (const m of all()) if (m.dataset.nyigenPremiumV23) finish(m); }
    else init();
  });
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  addEventListener('load', init);
  if (customElements?.whenDefined) customElements.whenDefined('header-menu').then(init);
  init();
})();